import { useEffect, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext.tsx";
import { FundoMarca, LOGO_MARCA } from "@/components/atomic-crm/misc/FundoMarca";
import { useMarcaDoLogin } from "@/components/atomic-crm/organizacoes/marcaDoLogin";
import { aplicarCor } from "@/components/atomic-crm/organizacoes/useOrganizacaoAtiva";

const getLoginErrorMessage = (error: unknown) => {
  const rawMessage =
    typeof error === "string"
      ? error
      : typeof error === "undefined" || !(error as any)?.message
        ? ""
        : String((error as any).message);

  const normalizedMessage = rawMessage.trim().toLowerCase();

  if (!normalizedMessage) {
    return "ra.auth.sign_in_error";
  }

  if (normalizedMessage.includes("invalid login credentials")) {
    return "E-mail ou senha inválidos.";
  }

  if (normalizedMessage.includes("email not confirmed")) {
    return "E-mail não confirmado.";
  }

  return rawMessage;
};

export const LoginPage = (props: { redirectTo?: string }) => {
  const { title: tituloNorth } = useConfigurationContext();
  // Escritório cliente (291): a marca dele; undefined = ainda carregando.
  const marca = useMarcaDoLogin();
  const title = marca === undefined ? "" : (marca?.nome ?? tituloNorth);
  const logo = marca === undefined ? null : marca ? marca.logo_url : LOGO_MARCA;
  const cor = marca?.cor_primaria;
  useEffect(() => (cor ? aplicarCor(cor) : undefined), [cor]);
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  // O hub é lazy: sem isto, o chunk dele só começa a baixar DEPOIS do login
  // aceito, e a pessoa fica olhando pro nada entre uma tela e outra. Aqui ele
  // baixa enquanto ela ainda está digitando a senha, então na hora de entrar já
  // está em memória. Falha de rede aqui não importa — o lazy tenta de novo
  // sozinho no caminho normal.
  useEffect(() => {
    void import("@/components/atomic-crm/hub/HubPage").catch(() => {});
  }, []);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(getLoginErrorMessage(error), { type: "error" });
      });
  };

  return (
    // Mesmo fundo e mesmo logo do hub, de propósito: ao entrar, o logo daqui
    // sobe e vira o do hub. Se esta tela fosse cinza, a transição seria um
    // corte de tela em vez de uma continuação.
    <FundoMarca cor={marca?.cor_primaria}>
      <div className="w-full max-w-md space-y-8 px-4">
        <div className="flex flex-col items-center space-y-3">
          {/* h-24 aqui contra h-14 lá com scale inicial de 1.9 — é daqui que o
              logo do hub parte. Mexeu num, confira o outro. */}
          {logo ? <img className="h-24 w-auto" src={logo} alt={title} /> : <div className="h-24" aria-hidden />}
          <h1 className="text-[11px] font-bold uppercase tracking-[0.16em] text-ouro">
            {title}
          </h1>
        </div>

        <div className="rounded-[14px] bg-white p-7 shadow-2xl ring-1 ring-black/5">
          <h2 className="mb-5 text-xl font-bold tracking-[-0.01em] text-marinho">
            {translate("ra-supabase.auth.sign_in", { _: "Entrar" })}
          </h2>
          <Form className="space-y-4" onSubmit={handleSubmit}>
            <TextInput
              label={translate("ra-supabase.auth.email", { _: "Email" })}
              source="email"
              type="email"
              validate={required()}
            />
            <TextInput
              label={translate("ra-supabase.auth.password_label", { _: "Password" })}
              source="password"
              type="password"
              validate={required()}
            />
            <Button
              type="submit"
              className="h-10 w-full cursor-pointer"
              disabled={loading}
            >
              {translate("ra-supabase.auth.sign_in", { _: "Entrar" })}
            </Button>
          </Form>

          <Link
            to={"/forgot-password"}
            className="mt-4 block text-center text-sm text-muted-foreground hover:underline"
          >
            {translate("ra-supabase.auth.forgot_password", { _: "Esqueceu a senha?" })}
          </Link>
        </div>
      </div>
      <Notification />
    </FundoMarca>
  );
};
